import type { ActionDefinition } from '../../types';
import type { ActionHandler, ActionContext } from '../../registry/ActionRegistry';

/**
 * Select option action definition
 */
export interface SelectOptionAction extends ActionDefinition {
  type: 'select-option';
  targetSelector: string;
  value: string;
  /** Timeout for finding element (ms) */
  timeout?: number;
}

/**
 * Handler for selecting dropdown options
 */
export class SelectOptionActionHandler implements ActionHandler<SelectOptionAction> {
  async execute(action: SelectOptionAction, context: ActionContext): Promise<void> {
    const { targetSelector, value, timeout = 5000 } = action;

    // Find element
    const element = await context.domAdapter.waitForElement(targetSelector, timeout);
    if (!element) {
      throw new Error(`Element not found: ${targetSelector}`);
    }

    // Verify it's a select element
    if (!(element instanceof HTMLSelectElement)) {
      throw new Error(`Element is not a select: ${targetSelector}`);
    }

    // Check if element is visible
    if (!context.domAdapter.isVisible(element)) {
      throw new Error(`Element not visible: ${targetSelector}`);
    }

    // Check if element is disabled
    if (context.domAdapter.isDisabled(element)) {
      throw new Error(`Element is disabled: ${targetSelector}`);
    }

    // Verify the option exists
    const option = Array.from(element.options).find((opt) => opt.value === value);
    if (!option) {
      const availableValues = Array.from(element.options)
        .map((opt) => opt.value)
        .join(', ');
      throw new Error(
        `Option value "${value}" not found. Available: ${availableValues}`
      );
    }

    // Check if the option is disabled
    if (option.disabled) {
      throw new Error(`Option "${value}" is disabled and cannot be selected`);
    }

    // Select the option
    context.domAdapter.selectOption(element, value);

    // Verify selection
    if (element.value !== value) {
      throw new Error(`Failed to select option. Expected: "${value}", Got: "${element.value}"`);
    }
  }

  validate(action: SelectOptionAction): boolean {
    return Boolean(action.targetSelector && typeof action.value === 'string');
  }
}
